/**
 * Group a role's assignable permissions by permission-catalog module.
 *
 * The role editor used to be organised by SCREEN: each access module carried a
 * switch that granted every permission behind it. That is the shape that
 * produced this month's escalation bugs — an access module's `anyOf` answers
 * "who may open this screen", so it necessarily mixes read with write, and
 * handing it out as a grant over-granted every time (ticking "Check-in codes"
 * attached admin.*).
 *
 * So the axis is inverted here. The catalog (module -> action) says what
 * capabilities exist; a role is built by ticking those capabilities; which
 * screens open is a CONSEQUENCE, derived and shown read-only. Nothing in this
 * file grants anything in bulk — that is the point.
 *
 * See AccessModuleDefaults (screens) vs PermissionCatalogDefaults (capabilities)
 * on the API side: two orthogonal axes, deliberately kept apart.
 */

/** Permissions the catalog deliberately does not describe: wildcards, platform. */
export const UNCATALOGUED_GROUP_KEY = '__uncatalogued__';

const slugOf = (row) => {
  if (!row) return '';
  if (row.resource && row.action) return `${row.resource}.${row.action}`;
  return String(row.name || '');
};

const permissionIdOf = (p) => String(p?.id ?? p?.permission_id ?? '');

/**
 * @param permissions rbac_permissions rows (already filtered of platform.*)
 * @param modules     rbac_permission_modules rows
 * @param actions     rbac_module_actions rows, the whole catalog
 * @returns groups in catalog sort order, each with its matched permissions.
 *   Modules that match no permission are dropped — an empty accordion panel
 *   is noise. A trailing "Not in the catalog" group collects whatever the
 *   catalog does not describe, so drift is visible here rather than silent.
 */
export const groupPermissionsByCatalog = (permissions = [], modules = [], actions = []) => {
  // slug -> module_key, from the catalog.
  const moduleOfSlug = new Map();
  (actions || []).forEach((a) => {
    const slug = slugOf(a);
    if (slug) moduleOfSlug.set(slug, a.module_key);
  });

  const buckets = new Map();
  const leftovers = [];

  (permissions || []).forEach((p) => {
    const slug = slugOf(p);
    const moduleKey = moduleOfSlug.get(slug);
    if (!moduleKey) {
      leftovers.push(p);
      return;
    }
    if (!buckets.has(moduleKey)) buckets.set(moduleKey, []);
    buckets.get(moduleKey).push(p);
  });

  const ordered = [...(modules || [])]
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || String(a.label).localeCompare(String(b.label)))
    .filter((m) => buckets.has(m.module_key))
    .map((m) => ({
      key: m.module_key,
      label: m.label || m.module_key,
      description: m.description || '',
      items: buckets.get(m.module_key),
      uncatalogued: false,
    }));

  // A permission whose module was deleted or deactivated still has to be
  // reachable — otherwise it silently disappears from the editor while
  // remaining attached to roles.
  const known = new Set((modules || []).map((m) => m.module_key));
  [...buckets.entries()]
    .filter(([moduleKey]) => !known.has(moduleKey))
    .forEach(([, items]) => leftovers.push(...items));

  if (leftovers.length) {
    ordered.push({
      key: UNCATALOGUED_GROUP_KEY,
      label: 'Not in the catalog',
      description:
        'These permissions exist but no catalog action describes them — usually created directly in the database. Add a matching action so they group properly.',
      items: leftovers,
      uncatalogued: true,
    });
  }

  return ordered;
};

/**
 * How many of a group's permissions are currently on.
 *
 * Reported, never actionable: there is deliberately no "select all in this
 * module" control. The admin ticks each capability they mean to give.
 */
export const groupSelectionCount = (group, assignedSet) => {
  const items = group?.items || [];
  const on = items.filter((p) => assignedSet.has(permissionIdOf(p))).length;
  return { on, total: items.length };
};

export { permissionIdOf, slugOf };
